/**
 * Assessment Workflow Configuration
 *
 * This module defines the schema, defaults, and per-organisation presets for the
 * assessment workflow configuration system.  It replaces ~15 files of hardcoded
 * org-name string checks with a single, Firestore-driven config that is stamped
 * onto each patient at creation time.
 *
 * HOW IT WORKS
 * ------------
 * 1. Each organisation has an `assessmentWorkflow` object in its Firestore
 *    document (organisations/{orgId}).
 * 2. When a new patient is created, that config is snapshot-copied onto the
 *    patient document as `workflowConfig`.
 * 3. At runtime, all workflow decisions read from the patient's stamped config.
 * 4. Existing patients without `workflowConfig` fall back to legacy behaviour
 *    derived from the org name (see `deriveLegacyConfig` in useWorkflowConfig).
 *
 * ADDING A NEW ORGANISATION
 * -------------------------
 * 1. Add an entry to `ORG_WORKFLOW_CONFIGS` below with the desired field values.
 * 2. Run the admin backfill script to write `assessmentWorkflow` to the org's
 *    Firestore document.
 * 3. All NEW patients for that org will automatically get the stamped config.
 *
 * UPGRADING AN EXISTING ORGANISATION (e.g. hierarchical → flat DF)
 * ----------------------------------------------------------------
 * 1. Update the org's entry in `ORG_WORKFLOW_CONFIGS`.
 * 2. Run the admin backfill script to update the Firestore org document.
 * 3. Only newly created patients pick up the change; existing patients are
 *    unaffected (their stamped config is immutable after creation).
 */

// ---------------------------------------------------------------------------
// Enum-like constants for valid field values
// ---------------------------------------------------------------------------

/**
 * Module ordering presets.  Determines the stamped workflow module sequence
 * (backend uses moduleOrder[0] === 'ssi' for is_ssi_first). Overview card
 * display order for appointment-finish-path orgs is derived separately in
 * patientOverviewConfig from workflowDecisions.ciFinishPath (padInAppointment
 * puts PAD ahead of DD; otherwise DD ahead of PAD).
 *
 * CI_FIRST: Clinical Interview → SSI → DD → PAD  (most organisations, including
 *   one-clinician SLaM / 3 Borough since Oct 2026)
 * SSI_FIRST: Semi-Structured Interview → CI → DD → PAD  (Urban ADHD, and every
 *   SLaM / 3 Borough patient stamped before the one-clinician model)
 */
export const MODULE_ORDERS = Object.freeze({
  CI_FIRST: ['ci', 'ssi', 'dd', 'pad'],
  SSI_FIRST: ['ssi', 'ci', 'dd', 'pad'],
});

/**
 * Appointment finish path — runtime choice recorded on
 * patient.workflowDecisions.ciFinishPath when the Lead finishes the LAST
 * interview module of the appointment (SLaM / 3 Borough).
 *
 * Which module asks the question depends on the stamped workflow (see
 * appointmentFinishPathModule in useWorkflowConfig):
 *   - legacy two-clinician SLaM (ciFlow.endPage reviewAndSynthesis): asked at
 *     the end of Clinical Interview Review & Synthesis, because the SSI was
 *     already done by another clinician in an earlier appointment;
 *   - one-clinician SLaM (ssiFlow.appointmentFinishPath enabled): asked at the
 *     end of the SSI Review & Synthesis, because the Lead runs CI then SSI in
 *     the same appointment.
 * The key keeps its historical name `ciFinishPath` so every downstream reader
 * (PAD provisional outcome, overview ordering, DD mismatch dialog, backend
 * pad_outcome_utils) works unchanged for both generations of patient.
 *
 * PAD_IN_APPOINTMENT: Clinician shares the outcome now and starts PAD in this
 *   appointment. Provisional positive/negative PAD is stored on the PAD stage;
 *   formal DD is completed later. Overview keeps PAD ahead of DD.
 * DEFER_PAD: Clinician ends the appointment, generates the interview report,
 *   and does DD then PAD in their own time. Overview flips to DD ahead of PAD.
 */
export const CI_FINISH_PATHS = Object.freeze({
  PAD_IN_APPOINTMENT: 'padInAppointment',
  DEFER_PAD: 'deferPad',
});

/**
 * End-of-CI decision — runtime choice recorded on
 * patient.workflowDecisions.ciEndDecision when a one-clinician SLaM Lead
 * finishes the Clinical Interview (ciFlow.endAssessmentDecision enabled).
 *
 * PROCEED_TO_SSI: Continue into the Semi-Structured Interview in the same
 *   appointment. The SSI card unlocks on the Patient Overview.
 * END_ASSESSMENT: Stop the assessment after the Clinical Interview. The CI
 *   is completed and its report generated (including the "Assessment
 *   Conclusion" paragraph drafted from the clinician's reason and optional
 *   recommended next steps, stored at the patient root as
 *   `assessmentEnd = { reason, nextSteps, endedAt, endedBy, endedByName,
 *   resumedAt, resumedBy, resumedByName }`); SSI, DD and PAD stay locked
 *   until the decision is reversed with "Resume assessment", which deletes
 *   this key. The Patient Report for an ended assessment is the "early exit"
 *   variant (template key EARLY_EXIT_AFTER_CI) and needs only the CI report
 *   approved — see utils/requiredSectionsUtils.js.
 *
 * Absent key = not yet decided. Only the CI finish flow and the Resume action
 * write it.
 */
export const CI_END_DECISIONS = Object.freeze({
  PROCEED_TO_SSI: 'proceedToSsi',
  END_ASSESSMENT: 'endAssessment',
});

/**
 * DF (Diagnostic Formulation) preference.  Controls which button is the
 * PRIMARY CTA in the CI completion dialog when the clinician finishes the
 * Clinical Interview.
 *
 * END_OF_CI:  "Complete Diagnostic Formulation now" is the primary CTA.
 * END_OF_SSI: "Proceed to SSI (defer DF)" is the primary CTA.
 *
 * This is a PREFERENCE, not a hard gate.  The clinician can always choose
 * either option at runtime.  For SSI-first orgs (SLaM), this field is
 * ignored because DF always happens after CI (SSI is already done).
 */
export const DF_PREFERENCES = Object.freeze({
  END_OF_CI: 'endOfCI',
  END_OF_SSI: 'endOfSSI',
});

/**
 * SSI access policy.  Determines how the Semi-Structured Interview module
 * is gated on the Patient Overview page and in backend stage progress.
 *
 * WHEN_ADHD:  SSI is recommended when ADHD is under consideration.
 *             For legacy patients (no workflowConfig), SSI is hard-locked
 *             until PPD = ADHD.  For new patients, SSI is accessible but
 *             shows a soft recommendation note.
 *
 * ALWAYS:     SSI is always accessible regardless of the CI formulation.
 *             Currently used by SSI-first orgs (SLaM, 3 Borough).
 *             Planned for WLC, TOC once flat DF is activated.
 */
export const SSI_ACCESS_POLICIES = Object.freeze({
  WHEN_ADHD: 'whenAdhd',
  ALWAYS: 'always',
});

/**
 * DF (Diagnostic Formulation) model.  Controls the layout and data model
 * of the Diagnostic Formulation page.
 *
 * HIERARCHICAL: Three panels — Provisional Primary Diagnosis (PPD),
 *               Secondary/Co-occurring Diagnoses (SCD), and Differentials.
 *               Used by most organisations (Phoenix, WLC, TOC, etc.).
 *
 * FLAT:         Single multi-select "Conditions under consideration" panel
 *               plus a formulation notes text area.  No PPD/SCD distinction.
 *               Used by SLaM, 3 Borough. Planned for WLC, TOC (not yet activated).
 */
export const DF_MODELS = Object.freeze({
  HIERARCHICAL: 'hierarchical',
  FLAT: 'flat',
});

/**
 * DD (Diagnostic Decision) style.  Controls which action button is visually
 * emphasised in the DD completion dialog and whether the module title
 * includes "(MDT Review)".
 *
 * STANDARD:    "Go to PAD" is the primary CTA.
 * MDT_REVIEW:  "Generate Report" is the primary CTA.  The DD module title
 *              on the Patient Overview page shows "Diagnostic Decision
 *              (MDT Review)".
 */
export const DD_STYLES = Object.freeze({
  STANDARD: 'standard',
  MDT_REVIEW: 'mdtReview',
});

/**
 * QbCheck reporting modes.  Distinguishes how QbCheck results are processed
 * and reviewed within the platform.
 *
 * AI_REVIEW:   Full pipeline — clinician uploads QbCheck images, the backend
 *              runs AI summarisation to produce paragraphs, and a reviewer
 *              must approve the outputs before they appear in the patient
 *              report.  Used by Phoenix, Spectrum Mind.
 *
 * PASSTHROUGH: Simplified pipeline — clinician manually enters scores from
 *              an off-platform QbCheck report.  No AI processing, no
 *              clinician review step.  Results are auto-approved and
 *              injected directly into the report appendix.  Used by TOC.
 */
export const QB_CHECK_MODES = Object.freeze({
  AI_REVIEW: 'ai-review',
  PASSTHROUGH: 'passthrough',
});

/**
 * Assessment Workflow Type.  Distinguishes between fundamentally different
 * assessment workflows that require distinct platform behaviour, report
 * rendering pipelines, and PAD flows.
 *
 * STANDARD:  The ThinkDivergent ADHD Assessment Workflow used by all
 *            current organisations.  Multi-section PAD (DIS/TRT/HLT/MED),
 *            standard renderPatientReportTemplate CF.
 *
 * TOC_ADHD:  The TOC ADHD Assessment Workflow.  Simplified two-page PAD
 *            ("Assessment Outcome" + "Review"), contract-aware conditional
 *            report template, renderPatientReportTemplateTOC CF.
 *
 * This field is patient-level (stamped at creation from the org config).
 * Legacy patients without this field default to STANDARD.
 */
export const ASSESSMENT_WORKFLOW_TYPES = Object.freeze({
  STANDARD: 'standard',
  TOC_ADHD: 'tocAdhd',
  SLAM_ADHD: 'slamAdhd',
  WLC_ADHD: 'wlcAdhd',
});

// ---------------------------------------------------------------------------
// Module Flow Objects — nested per-module config (future canonical shape)
// ---------------------------------------------------------------------------
// These nested objects follow the {module}Flow: { property: value } pattern.
// They are designed as the canonical future shape that existing flat fields
// (ddStyle, dfPreference, ssiAccess) will eventually migrate into.
// For now, existing flat fields are kept untouched — these are additive.

/**
 * SSI Flow — controls Semi-Structured Interview module behaviour.
 *
 * endPage: Which page concludes the SSI module.
 *   RDD:                   Standard Recommended Diagnosis Decision page with
 *                          DSM-5 criteria review and formal ADHD recommendation.
 *   REVIEW_AND_SYNTHESIS:  Review & Synthesis page with emerging differentials,
 *                          formulation notes, and confidential observations.
 *
 * leadOverride: Whether the Lead clinician may take over SSI module ownership
 *   from the assigned SSI clinician (edit decisions/notes + review AI outputs)
 *   after the SSI has been completed. Used by SLaM / 3 Borough so the Lead can
 *   reconcile symptom/domain decisions with the final diagnostic outcome without
 *   looping the original SSI clinician back in. Report attribution and the
 *   original appointment date stay with the original clinicianSSI slot.
 *   DISABLED: Override UI and cloud function are unavailable (default).
 *   ENABLED:  Lead sees an Override button on the SSI module card and on the
 *             DD consistency banner; proceeding snapshots the original record
 *             and transfers effective SSI ownership to the Lead.
 */
export const SSI_END_PAGES = Object.freeze({
  RDD: 'rdd',
  REVIEW_AND_SYNTHESIS: 'reviewAndSynthesis',
});

export const SSI_LEAD_OVERRIDE_STATES = Object.freeze({
  DISABLED: 'disabled',
  ENABLED: 'enabled',
});

/**
 * SSI Flow — controls whether the "Domains Impacted" multi-select chip rows
 * are shown on each criterion's Childhood/Adulthood panels.
 *
 *   ENABLED:  Standard behaviour. Domain chip rows and the Review-page domain
 *             impact summary table are rendered. Domain selections feed into
 *             LLM paragraph generation as "priority 3" context (after evidence
 *             levels and clinician notes).
 *   DISABLED: Domain chip rows hidden from criterion panels; domain impact
 *             table hidden from the SSI Review page (symptom summary table
 *             widens to fill the space). Existing domain data on the patient
 *             document is preserved but not displayed. The LLM pipeline
 *             degrades gracefully ("Domains Affected: None specified").
 *
 * This is patient-level (stamped at creation from the org config). Patients
 * without an explicit ssiFlow.domainsImpacted default to ENABLED, so existing
 * patients and orgs that have not opted out keep domains visible.
 */
export const SSI_DOMAINS_IMPACTED_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * SSI Flow — controls whether the Emerging Differentials panel on the
 * Review & Synthesis page is a mandatory gate or an optional input.
 *
 *   REQUIRED: The clinician must confirm at least one non-ADHD emerging
 *             differential before the page can be completed. This is the
 *             TOC behaviour and the default for every workflow type except
 *             WLC.
 *   OPTIONAL: The clinician can complete the Review & Synthesis page
 *             without selecting any differentials or clicking Confirm.
 *             The panel is still visible (so clinicians can record
 *             differentials if they choose to), but it does not gate the
 *             Complete button. When no differentials are confirmed,
 *             handleComplete writes an empty array and the DD module
 *             skips the Other Conditions step.
 *
 * Patient-level (stamped at creation from the org config). Absent key
 * resolves at runtime via the wlcAdhd fallback in useWorkflowConfig:
 * optional for WLC patients, required for everyone else.
 */
export const SSI_EMERGING_DIFFERENTIALS_STATES = Object.freeze({
  REQUIRED: 'required',
  OPTIONAL: 'optional',
});

/**
 * SSI Flow — whether the Semi-Structured Interview is the module that asks
 * the appointment finish question (ssiFlow.appointmentFinishPath).
 *
 *   ENABLED:  Finishing SSI Review & Synthesis opens the appointment finish
 *             wizard (share the outcome now and start the PAD, or end the
 *             appointment and generate the report), writing
 *             workflowDecisions.ciFinishPath. Used by one-clinician SLaM /
 *             3 Borough, where the Lead runs CI then SSI in one appointment
 *             and the SSI is therefore the last interview of the day.
 *   DISABLED: SSI Review & Synthesis finishes as it does today for the org
 *             (Generate Report, or Proceed to Diagnostic Decision).
 *
 * Patient-level, stamp only. Absent -> DISABLED, so every existing patient
 * (including legacy two-clinician SLaM, whose wizard lives at the end of the
 * CI via ciFlow.endPage) is untouched. See CI_FINISH_PATHS.
 */
export const SSI_APPOINTMENT_FINISH_PATH_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * CI Flow — controls Clinical Interview module behaviour.
 *
 * endPage: Which page concludes the CI module (section 12).
 *   DIAGNOSTIC_FORMULATION:  Standard DF page with condition selection (PPD/SCD
 *                            or flat differentials depending on dfModel).
 *   REVIEW_AND_SYNTHESIS:    Review & Synthesis page with emerging differentials,
 *                            formulation notes, and confidential observations.
 *                            Used when both SSI and CI clinicians independently
 *                            record their clinical synthesis (e.g. SLaM).
 */
export const CI_END_PAGES = Object.freeze({
  DIAGNOSTIC_FORMULATION: 'diagnosticFormulation',
  REVIEW_AND_SYNTHESIS: 'reviewAndSynthesis',
});

/**
 * CI Flow — Risk Assessment stage on/off.
 *
 * Controls whether the Risk Assessment step is part of the Clinical Interview
 * module. In the Adult Autism app the CI module is displayed as "History", and
 * Risk Assessment is the clinician-only step appended after the prefill sections
 * (stored at assessmentStages/history.riskAssessment).
 *
 * ENABLED:  Standard behaviour. Risk Assessment appears in the History sidebar,
 *           its paragraph (asd_ci_RSK_summary) is generated, and the risk block
 *           renders in the CI report + Patient Report.
 * DISABLED: The Risk Assessment stage is turned off entirely — hidden from the
 *           History sidebar (so the module ends at Psychiatric History and the
 *           forward CTA goes straight to the Semi-Structured Interview), its
 *           paragraph is not generated (suppressed like ci_DF / pad_* codes),
 *           and HAS_RISK_ASSESSMENT is never activated at render so the report's
 *           conditional risk block is dropped. Used by organisations (e.g. TOC)
 *           whose clinicians record risk on a separate patient-management
 *           platform. Modelled on padFlow.agreedMedicationPlan.
 *
 * This is patient-level (stamped at creation from the org config). Patients
 * without an explicit ciFlow.riskAssessment default to ENABLED, so legacy
 * patients keep Risk Assessment untouched.
 */
export const CI_RISK_ASSESSMENT_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * CI Flow — end-of-CI "proceed or end the assessment" decision
 * (ciFlow.endAssessmentDecision).
 *
 *   ENABLED:  When the clinician finishes the Clinical Interview (Risk
 *             Assessment is the last CI page because DF is deferred to the
 *             SSI), a decision dialog asks whether to proceed to the
 *             Semi-Structured Interview or to end the assessment here with a
 *             written reason. The answer is recorded on
 *             workflowDecisions.ciEndDecision (see CI_END_DECISIONS). Until
 *             "proceed" is recorded the SSI card stays locked; once "end" is
 *             recorded SSI, DD and PAD are locked and the CI report gains an
 *             "Assessment Conclusion" paragraph. Used by one-clinician SLaM /
 *             3 Borough.
 *   DISABLED: No decision dialog; finishing the CI goes straight to the SSI
 *             as today.
 *
 * Patient-level, stamp only. Absent -> DISABLED, so WLC, TOC, LMP and every
 * legacy patient keep the existing CI completion dialog.
 */
export const CI_END_ASSESSMENT_DECISION_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * OBS Flow — Adult Autism Behavioural Observation module on/off.
 *
 * Controls whether the Behavioural Observation stage is part of an org's Adult
 * Autism assessment.
 *
 * ENABLED:  Standard behaviour. The Observation module appears on the autism
 *           Patient Overview, its report is a prerequisite for the Diagnostic
 *           Decision, it counts toward Patient Report finalisation, and the
 *           optional Observation clinician doubles as the DD/MDT supporting
 *           clinician.
 * DISABLED: The Observation stage is turned off — hidden from the autism
 *           Patient Overview, Documents Dashboard, and dashboard pills; it is
 *           no longer a DD prerequisite or part of the Patient Report approval
 *           set; its paragraphs are suppressed and HAS_OBSERVATION is not
 *           activated at render; and no Observation clinician is requested (DD
 *           supporting becomes a normal optional MDT supporter). Used by orgs
 *           (e.g. Lancaster Medical Practice) that do not run a separate
 *           Behavioural Observation appointment. Modelled on
 *           ciFlow.riskAssessment.
 *
 * This is patient-level (stamped at creation from the org config). Patients
 * without an explicit obsFlow.observation default to ENABLED, so TOC and every
 * existing autism patient keep Observation untouched.
 */
export const OBS_OBSERVATION_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * OBS Flow — Adult Autism Behavioural Observation module VERSION.
 *
 * Controls which Behavioural Observation experience a patient gets. This is a
 * self-contained cutover flag (like `replaceCARwithReportBuilder`): it is read
 * from the patient's immutable stamped config only, with NO legacy org-name
 * fallback, so existing/in-flight patients are never silently upgraded.
 *
 * V1: The original task-by-task Criterion Mapping (drag notes onto each task's
 *     criteria; evidence recorded per task × criterion at
 *     `allTaskData.{taskId}.dsm_{criterionId}_evidence`). LLM inputs come from
 *     those per-task mapped notes. This is what every existing autism patient
 *     has, so absent `obsFlow.version` MUST resolve to V1.
 * V2: The approved redesign — clinicians tag notes to DSM criteria (and the
 *     Self-Reported / Other Behaviours buckets) while in Task Administration
 *     and ADOS-informed Coding, then work criteria-by-criteria in Criterion
 *     Mapping recording a single evidence decision per criterion at
 *     `data.criteriaEvidence.{criterionId}` plus optional `data.criteriaNotes`.
 *     The LLM pipeline consumes tagged notes + criteria evidence + criteria
 *     notes and adds a conditional "Other Behaviours" paragraph.
 *
 * Patient-level (stamped at creation from the org config). Absent -> V1.
 */
export const OBS_VERSIONS = Object.freeze({
  V1: 'v1',
  V2: 'v2',
});

/**
 * DD Flow — controls Diagnostic Decision module structure.
 *
 * structure: How many pages and what navigation the DD module uses.
 *   STANDARD:    1–2 pages (ADHD decision + optional other conditions).
 *               No sidebar. Module completes on ADHD or Other Conditions page.
 *   MULTI_STEP:  Up to 4 pages with sidebar navigation (Review → ADHD →
 *               optional Differentials → Final Formulation). Module completes
 *               on the Formulation page.
 */
export const DD_STRUCTURES = Object.freeze({
  STANDARD: 'standard',
  MULTI_STEP: 'multiStep',
});

/**
 * DD Differentials Decisions — whether clinicians must make per-condition
 * decisions (Provide next steps / Rule out) on emerging differentials during
 * the Diagnostic Decision module.
 *
 * ENABLED:  Standard behaviour. The "Decision on Differentials" / "Decision
 *           for Other Conditions" page is shown, and clinicians must choose
 *           a decision for each non-primary condition before completing DD.
 * DISABLED: The per-condition decision step is skipped entirely. Emerging
 *           differentials are shown as a read-only list on the Final
 *           Formulation page so clinicians can reference them when writing
 *           their formulation, but no per-condition decisions are recorded.
 *           Used by The Owl Centre.
 */
export const DD_DIFFERENTIALS_DECISIONS_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

/**
 * DD MDT-information email gate (ddFlow.mdtInfoEmailGate) — when the
 * "Information Available ahead of MDT Review" email may be sent to the
 * DD-stage supporting clinicians. Read server-side only
 * (send_ssi_completion_email in notification_service.py); the client never
 * branches on it. It lives here so the org presets, the dev-mode validator
 * and the Python mirror stay in lockstep.
 *
 *   SSI_APPROVED:          Send as soon as the SSI report is approved
 *                          (historical behaviour; default when absent). For
 *                          two-clinician orgs where one clinician does the CI
 *                          and another the SSI (Phoenix, SLaM, 3 Borough,
 *                          Urban ADHD).
 *   BOTH_REPORTS_APPROVED: Send only once both the CI and SSI reports are
 *                          approved, whichever is approved last. For
 *                          single-clinician orgs where the same person does
 *                          CI+SSI (TOC, WLC).
 */
export const MDT_INFO_EMAIL_GATES = Object.freeze({
  SSI_APPROVED: 'ssiApproved',
  BOTH_REPORTS_APPROVED: 'bothReportsApproved',
});

/**
 * DD hidePartiallyMet (ddFlow.hidePartiallyMet, plain boolean) — whether the
 * adult ADHD "Decision for ADHD" offers "Partially met" as an answer.
 *
 *   true:           the radio shows only "Fully met" and "Not met"; the
 *                   follow-ups that depend on a partial answer (diagnostic
 *                   classification, other specified reason) are never reached.
 *   false / absent: "Partially met" is offered, as today for every org.
 *
 * Read from the patient's stamped workflowConfig ONLY and compared to exactly
 * true (see ddHidePartiallyMet in useWorkflowConfig). An absent key therefore
 * keeps the option visible: a patient who already recorded "Partially met"
 * keeps that answer, and every report / analytics path that reads the stored
 * string is unchanged. Deliberately a boolean rather than an enabled /
 * disabled enum, because several of those enums treat a missing key as the
 * newer behaviour and this one must not. The same key lives on cypAdhdFlow
 * for the CYP Decision for ADHD; each pathway flips independently. No adult
 * org sets this yet (awaiting confirmation); flipping one later is a preset
 * edit plus `backfill_org_documents.py --workflow`, with no code change.
 */

/**
 * PAD Flow — controls Post-Assessment Discussion module behaviour.
 *
 * agreedMedicationPlan: Whether the "Agreed Medication Plan" (AMP) stage is
 *   part of this org's PAD.
 *   ENABLED:  Standard behaviour. When the clinician answers "Yes" to
 *             "Create medication plan now?" in Health Checks, the PAD
 *             continues into the AMP stage (medication + titration schedule).
 *   DISABLED: The AMP stage is turned off entirely. AMP is hidden from the
 *             PAD sidebar, the "Yes" option is removed from "Create medication
 *             plan now?" (leaving only the "No - defer" / "No - refer on to
 *             titration" options), and the AMP route redirects away. The PAD
 *             therefore ends at Health Checks. Used by Well Life Clinic and
 *             SLaM / 3 Borough, whose medication planning happens off-platform.
 *
 * stageOrder: Clinician-facing order of the Strategies and Treatment Options
 *   stages in the PAD proforma (does NOT change report/document section order).
 *   STRATEGIES_FIRST: Review → Strategies → Treatment Options → … (default).
 *   TREATMENT_FIRST:  Review → Treatment Options → Strategies → … Used by
 *                     SLaM / 3 Borough. The "Carry out health checks now?"
 *                     gate UI moves to the end of Strategies (still writing
 *                     the same Firestore path on Treatment Options).
 *
 * This is patient-level (stamped at creation from the org config). Patients
 * without an explicit padFlow fall back to the org-name legacy derivation,
 * so the setting applies to existing patients too without a data migration.
 */
export const PAD_MEDICATION_PLAN_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

export const PAD_STAGE_ORDERS = Object.freeze({
  STRATEGIES_FIRST: 'strategiesFirst',
  TREATMENT_FIRST: 'treatmentFirst',
});

/**
 * PAD structure — which shape the Post-Assessment Discussion module takes.
 *
 * NEXT_STEPS: Simplified 2-section PAD (Assessment Outcome + Next Steps page
 *   with boolean toggles for template keys). Used by The Owl Centre.
 * FULL:       Standard multi-section PAD (Review of Assessment, Strategies &
 *   Recommendations, Treatment Options, Health Checks, and optionally AMP).
 *   Used by SLaM, Well Life, Phoenix, and Lancaster Medical Practice (which
 *   runs tocAdhd but needs a full DIS/TRT/HLT PAD rather than Next Steps).
 *
 * Resolution rule mirrors ddFlow.structure:
 *   padFlow.structure || (isTOCWorkflow ? 'nextSteps' : 'full')
 */
export const PAD_STRUCTURES = Object.freeze({
  NEXT_STEPS: 'nextSteps',
  FULL: 'full',
});

// ---------------------------------------------------------------------------
// CYP ADHD Flow — per-org config for the CYP ADHD pathway modules
// ---------------------------------------------------------------------------
// The CYP ADHD pathway (assessmentModule 'cypAdhd') shares the org's single
// assessmentWorkflow object with the adult pathways, so its keys live in a
// CYP-distinct `cypAdhdFlow` namespace: CYP pages read ONLY the wf.cyp*
// helpers derived from this object and NEVER the adult ssiFlow / ddFlow /
// padFlow keys or assessmentWorkflowType-conditional defaults. This keeps the
// two pathways independently tunable per org — a change to either pathway's
// keys can never alter the other's behaviour.
//
// Keys exist only where a CYP page reads them; each carries the full set of
// legal values it has today (some enums are single-member by design — add
// members when a real variation is decided, never speculatively).

/**
 * CYP SSI end page.  Which page concludes the CYP Semi-Structured Interview.
 *
 * REVIEW_AND_SYNTHESIS: Review & Synthesis page with emerging differentials,
 *                       emerging formulation notes, and confidential notes —
 *                       the only CYP SSI ending (mirrors adult WLC V2).
 */
export const CYP_SSI_END_PAGES = Object.freeze({
  REVIEW_AND_SYNTHESIS: 'reviewAndSynthesis',
});

/**
 * CYP SSI emerging differentials requirement.  Whether the Emerging
 * Differentials panel on the CYP SSI Review & Synthesis page gates the
 * Complete button.
 *
 * OPTIONAL: Panel visible but never gates Complete (Well Life Clinic).
 * REQUIRED: At least one non-ADHD differential must be confirmed before
 *           the page can be completed.
 *
 * Resolution is CYP-explicit: absent cypAdhdFlow.emergingDifferentials
 * resolves to OPTIONAL. It deliberately does NOT reuse the adult
 * ssiFlow.emergingDifferentials value or the wlcAdhd conditional default.
 */
export const CYP_SSI_EMERGING_DIFFERENTIALS_STATES = Object.freeze({
  OPTIONAL: 'optional',
  REQUIRED: 'required',
});

/**
 * CYP DD structure.  The shape of the CYP Diagnostic Decision module.
 *
 * FINAL_FORMULATION: Multi-step wizard ending at Final Formulation:
 *                    MDT Review (read-only consolidation) → diagnostic
 *                    decision → other conditions (skipped when no emerging
 *                    differentials) → final formulation. There is no MDT
 *                    Plan step (descoped from the initial CYP version).
 */
export const CYP_DD_STRUCTURES = Object.freeze({
  FINAL_FORMULATION: 'finalFormulation',
});

// Whether the CYP Diagnostic Decision asks for a per-condition decision on
// each emerging differential (cypAdhdFlow.differentialsDecisions):
//   ENABLED — the "Decision on Differentials" step is part of the wizard:
//     every emerging differential from the SSI gets Confirm / Rule out /
//     Unable to confirm plus notes, and the Final Formulation shows those
//     decisions read-only (WLC V2; the original behaviour). Absent → ENABLED.
//   DISABLED — the step is skipped entirely, exactly as the adult ADHD and
//     adult Autism DDs do for TOC: the sidebar has no Decision on
//     Differentials row, Decision for ADHD goes straight to Final
//     Formulation, and the emerging differentials appear there as a
//     read-only reference list that is not included in the report (TOC,
//     Oct 2026 — parity with their adult pathways).
// Same two states as the adult ddFlow.differentialsDecisions, but a separate
// key in the CYP namespace: CYP pages never read the adult key, so a change
// to either pathway cannot alter the other.
export const CYP_DD_DIFFERENTIALS_DECISIONS_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

// Whether the CYP "Decision for ADHD" offers "Partially met"
// (cypAdhdFlow.hidePartiallyMet, plain boolean):
//   true — hidden; the radio is Fully met / Not met only (WLC and TOC,
//     Oct 2026). Stamped onto new patients only.
//   false / absent — offered, so a child stamped before this key existed
//     keeps the answer they may already have recorded.
// Compared to exactly true from the stamp alone (cypDdHidePartiallyMet) —
// never from the org name — and independent of the adult
// ddFlow.hidePartiallyMet. See that key's comment for why it is a boolean.

// How firmly the CYP guardian is held at the school-representative nomination
// gate (cypAdhdFlow.schoolRepNomination):
//   OPTIONAL — nominate or skip with any reason (WLC; the original behaviour).
//   REQUIRED_WHEN_IN_SCHOOL — a child who is in school, nursery or college
//     MUST have a school representative nominated before the guardian's
//     questionnaires unlock; the only way past without one is answering that
//     the child is not in school (TOC requirement, Sept 2026 — the
//     not-in-school path will later require an alternative informant, a
//     deferred build). The policy is stamped onto the guardian's informant
//     profile at creation (schoolRepNominationPolicy) because informants
//     cannot read the patient's workflowConfig.
export const CYP_SCHOOL_REP_NOMINATION_STATES = Object.freeze({
  OPTIONAL: 'optional',
  REQUIRED_WHEN_IN_SCHOOL: 'requiredWhenInSchool',
});

// The shape of the Risk Assessment step in the CYP Clinical Interview
// (cypAdhdFlow.riskAssessment):
//   STANDARD — the original panel (risks to self / to others / other risks),
//     shared with the Adult Autism History (WLC).
//   STRUCTURED_V1 — the NEST-parity structured assessment built for TOC
//     (Sept 2026), also used by WLC V2:
//     six Yes/No categorised sections with detail options, a "For
//     Consideration" notice with clinician acknowledgement, and protective
//     factors that never trigger the notice. Config lives in
//     src/pages/providers/19_cypAdhdCI/config/cypAdhdStructuredRiskConfig.js.
// Distinct from the adult ciFlow.riskAssessment on/off switch — the CYP Risk
// Assessment is always ON; this key only chooses its shape.
export const CYP_RISK_ASSESSMENT_STRUCTURES = Object.freeze({
  STANDARD: 'standard',
  STRUCTURED_V1: 'structuredV1',
});

// Behavioural Observation Appointment capture (cypAdhdFlow.boaCapture):
//   DISABLED — no BOA anywhere (WLC; the BOA is a TOC contractual concept).
//   MDT_REVIEW_TAB — a Behavioural Observation step at the start of the CYP
//     Diagnostic Decision asking whether a BOA took place and, if so,
//     capturing the four-paragraph summary (context, core observations,
//     client voice, differentials/signposting/feedback). Capture only:
//     deliberately NOT wired into report generation yet (agreed 23 Sept —
//     the report section follows once TOC's template decisions land).
export const CYP_BOA_CAPTURE_STATES = Object.freeze({
  DISABLED: 'disabled',
  MDT_REVIEW_TAB: 'mdtReviewTab',
});

// Symptom onset capture in the CYP Semi-Structured Interview
// (cypAdhdFlow.symptomOnsetCapture):
//   THIRTEEN_PLUS_BEFORE_ONLY — "present before turned 12" is asked per
//     symptom only for a child aged 13 or over; a younger child is inside the
//     age-12 window, so onset is implied by persistence and the question is
//     skipped (WLC; the original behaviour).
//   ALL_AGES_BEFORE_AFTER_PAIR — every child is asked both "present before
//     turned 12" and "present after turned 12" per symptom, recorded as a
//     linked pair (TOC, TDI-205: the pair evidences the childhood-onset
//     criterion for referrers). The after-12 half is shown as not applicable
//     for a child who has not yet turned 12 — nothing is stored for it, so
//     the pair stays honest and derivable from the child's age.
export const CYP_SYMPTOM_ONSET_CAPTURE_STATES = Object.freeze({
  THIRTEEN_PLUS_BEFORE_ONLY: 'thirteenPlusBeforeOnly',
  ALL_AGES_BEFORE_AFTER_PAIR: 'allAgesBeforeAfterPair',
});

// Supporting clinician at the CYP MDT Review (cypAdhdFlow.mdtSupportingClinician):
//   DISABLED — the Diagnostic Decision is recorded against the lead clinician
//     alone; no add-MDT-clinician affordance on the patient overview (WLC).
//   ENABLED — the lead clinician (or Admin/Support) can add a supporting
//     clinician to the Diagnostic Decision from the MDT Review card on the
//     patient overview, exactly as on the adult pathways, so a jointly made
//     MDT decision records both clinicians (TOC process, TDI-208: the lead
//     brings a supporting clinician into the MDT meeting and the diagnostic
//     decision is made jointly).
// Reuses the platform's existing DD supporting-clinician machinery
// (ReassignCliniciansDialog 'addDDSupporting' + the reassignClinicians
// callable) — this key only switches the affordance on for the CYP overview.
export const CYP_MDT_SUPPORTING_CLINICIAN_STATES = Object.freeze({
  DISABLED: 'disabled',
  ENABLED: 'enabled',
});

// What fills the "mental state" slot of the CYP ADHD assessment
// (cypAdhdFlow.mse). WLC and TOC choose between a pair of stamps. No CYP
// ADHD patient exists yet, so nothing reads an older name: the string
// clinicalObservationV1 is not a stamp.
//   NO_OBSERVATION_IN_SSI — the Semi-Structured Interview does not open with
//     a Clinical Observation. The Clinical Interview keeps its Mental State
//     Examination, and the questions are Jasmine's child-specific Clinical
//     Observation (Oct 2026), judged against age and developmental level
//     (src/pages/providers/19_cypAdhdCI/config/cypAdhdClinicalObservationConfig.js).
//     The step is still titled "Mental State Examination". Answers live on
//     the CI stage's mse field. This is what WLC and TOC stamp.
//   OBSERVATION_IN_SSI — the other one of the pair. The Clinical Interview
//     has no MSE step. The Semi-Structured Interview opens with a Clinical
//     Observation phase: light activities with the child, notes tagged to the
//     18 DSM-5 ADHD symptoms
//     (src/pages/providers/22_cypAdhdSSI/config/cypAdhdObservationConfig.js).
//     Notes live on the SSI stage under data.observation. No org stamps this.
//     Set an org's mse to it to turn the phase on.
//   ADULT_MIRROR — the adult ADHD MSE transcribed verbatim, as a CI step
//     (src/pages/providers/19_cypAdhdCI/config/cypAdhdMseConfig.js). Used only
//     when the key is absent.
export const CYP_MSE_STRUCTURES = Object.freeze({
  ADULT_MIRROR: 'adultMirror',
  NO_OBSERVATION_IN_SSI: 'noObservationInSsi',
  OBSERVATION_IN_SSI: 'observationInSsi',
});

// Treatment contraindication screening on the CYP ADHD Clinical Interview
// (cypAdhdFlow.medicalScreening). Clinician Yes/No + detail decisions for the
// NICE NG87 cardiac and medical checks, captured on the CI's Medical History
// and Family History steps, read back on the MDT Review and the PAD Health
// Checks page. Clinician-recorded structured data only: it is never fed to
// the AI drafting step and never written into a report.
//   DISABLED — no panel, no read-outs. Used when the key is absent, so every
//     legacy stamp and every org that has not opted in is unaffected. TOC
//     stamps this explicitly until requirements gathering says otherwise.
//   CONTRAINDICATIONS_V1 — the ten-item list (nine Medical History, one
//     Family History) in
//     src/pages/providers/19_cypAdhdCI/config/cypAdhdMedicalScreeningConfig.js.
//     Versioned because the item list is a clinical specification: a revised
//     list becomes a new state rather than a silent change to patients
//     already stamped V1. WLC stamps this (Oct 2026).
export const CYP_MEDICAL_SCREENING_STATES = Object.freeze({
  DISABLED: 'disabled',
  CONTRAINDICATIONS_V1: 'contraindicationsV1',
});

// ---------------------------------------------------------------------------
// Appointment Checklist — per-org toggle for the start/end-of-appointment
// checklist UI shown on Adult Autism proforma pages (History, SSI, OBS).
// ---------------------------------------------------------------------------

/**
 * Controls whether the "Start of Appointment" / "End of Appointment" checklist
 * pop-up is available on Adult Autism proforma pages.
 *
 * ENABLED:  The Breadcrumb row includes two ghost buttons ("Start of Appt." /
 *           "End of Appt.") that open a tabbed Radix Dialog with the numbered
 *           checklist items. The Start checklist auto-opens when navigating to
 *           DDI or OBS from the Patient Overview "Start/Continue" action.
 * DISABLED: No checklist UI is rendered. Default for all orgs that have not
 *           opted in.
 *
 * This is a frontend-only UI feature — no backend pipeline reads this field.
 * Absent → disabled (safe default). Only orgs explicitly stamped 'enabled'
 * (currently The Owl Centre and Demo Org) show the checklist.
 *
 * Resolution (padFlow-style three-tier):
 *   1. Patient's stamped appointmentChecklist (new patients after org backfill)
 *   2. Legacy derivation from org name (existing stamped TOC/Demo patients
 *      whose workflowConfig predates this field, plus fully legacy patients)
 *   3. { enabled: false } (safety net for unknown orgs)
 */
export const APPOINTMENT_CHECKLIST_STATES = Object.freeze({
  ENABLED: 'enabled',
  DISABLED: 'disabled',
});

// ---------------------------------------------------------------------------
// Supplementary Documents — optional features that augment the core modules
// ---------------------------------------------------------------------------

/**
 * Default supplementary documents configuration.  All features disabled.
 *
 * Each key represents a supplementary document type.  The value is an object
 * with at least an `enabled` boolean.  Some types support additional fields
 * (e.g. `mode` for QbCheck).
 *
 * This map is extensible: adding a new objective test or supplementary
 * document in the future only requires adding a new key here and in the
 * per-org configs — no structural changes to the resolution logic.
 *
 * Stamped onto each patient at creation time as part of `workflowConfig`.
 * For legacy patients without this field, `deriveLegacyConfig` reproduces
 * the correct values from org-name matching.
 */
export const DEFAULT_SUPPLEMENTARY_DOCUMENTS = Object.freeze({
  qbCheck: Object.freeze({ enabled: false }),
  qbTest: Object.freeze({ enabled: false }),
  bupaLetter: Object.freeze({ enabled: false }),
  preAssessmentReport: Object.freeze({ enabled: false }),
  // Semble MDT Report — copy-paste doc for MDT Review orgs that record
  // outcomes in the Semble EHR.  Surfaces Questionnaire Results (rules-based
  // scale text), History (reviewed AI summary + verbatim approved CI
  // paragraphs) and Plan (goals + To-discuss-in-PAD lists) once the
  // diagnostic decision is complete.  Currently enabled for Well Life Clinic.
  sembleMdtReport: Object.freeze({ enabled: false }),
});

// ---------------------------------------------------------------------------
// Appointment dates — per-module appointment date capture on Patient Overview
// ---------------------------------------------------------------------------

/**
 * Default appointment-dates feature flag.  Off for all orgs unless explicitly
 * enabled.  When enabled, clinicians / Support / Org Admins can set a calendar
 * appointment date on each ADHD assessment module (CI, SSI, DD, PAD); those
 * dates are preferred over inferred completion timestamps when rendering
 * reports.
 *
 * Currently enabled for SLaM / 3 Borough only.  Resolved at runtime via the
 * padFlow-style fallback in useWorkflowConfig so existing patients of gated
 * orgs get the UI without a patient-level backfill.
 */
export const DEFAULT_APPOINTMENT_DATES = Object.freeze({
  enabled: false,
});

// ---------------------------------------------------------------------------
// Default config — matches Phoenix / most organisations' CURRENT behaviour
// ---------------------------------------------------------------------------

/**
 * The default workflow configuration applied when an organisation has no
 * explicit config in Firestore.  Matches the traditional Phoenix workflow:
 *   CI (with DF at end) → SSI (gated on ADHD PPD) → DD → PAD
 */
export const DEFAULT_WORKFLOW_CONFIG = Object.freeze({
  moduleOrder: MODULE_ORDERS.CI_FIRST,
  dfPreference: DF_PREFERENCES.END_OF_CI,
  ssiAccess: SSI_ACCESS_POLICIES.WHEN_ADHD,
  dfModel: DF_MODELS.HIERARCHICAL,
  ddStyle: DD_STYLES.STANDARD,
  assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.STANDARD,
  // When true on a patient's stamped workflowConfig, Manage Documents uses the
  // progressive Report Builder shell (no standalone Cover Page row / generation).
  // Absent or false keeps Clinical Assessment Report + Cover Page.
  // SLaM Phase 2 cutover: set true on the org assessmentWorkflow only — existing
  // Phase 1 patient stamps are immutable and keep Cover Page.
  replaceCARwithReportBuilder: false,
  ciExclusions: [],
  supplementaryDocuments: DEFAULT_SUPPLEMENTARY_DOCUMENTS,
  appointmentDates: DEFAULT_APPOINTMENT_DATES,
});

// ---------------------------------------------------------------------------
// Per-organisation configs — representing each org's CURRENT behaviour
// ---------------------------------------------------------------------------
// This map serves multiple roles:
//   (a) A reference / single source of truth for developers to see each
//       org's intended workflow at a glance.
//   (b) Input for the Python-side admin backfill script (which has its
//       own mirror in python-functions/constants/workflow_config.py) to
//       seed the Firestore `assessmentWorkflow` field on org documents.
//   (c) Dev-mode validation (below) to catch typos in enum values.
//   (d) Runtime preset fallback in `resolveWorkflowConfig` for callers
//       that pass only an org name and no patient model (e.g. dashboard
//       progress pills, SSI-first detection, optimistic new-patient
//       summaries). Without this, orgs whose names do not match the
//       legacy substring checks (e.g. Urban ADHD) would resolve to the
//       wrong config. See useWorkflowConfig.js for the resolution order.
//
// When an org is ready to adopt new features (e.g. flat DF, deferred DF),
// update their config here AND in the Python mirror, then run the admin
// backfill script against the target environment's Firestore.
// Only newly created patients will pick up the change.

// ── Well Life Clinic ADHD V2 body ───────────────────────────────────────────
// The upgraded WLC workflow: adult ADHD (flat DF, deferred DF at end of SSI,
// SSI always accessible, multi-step DD, conditional report pipeline, Report
// Builder UI, appointment dates) plus the CYP ADHD pathway.
//
// Mid-assessment WLC patients are unaffected: their stamped workflowConfig is
// immutable, and unstamped legacy patients resolve via deriveLegacyConfig()
// in useWorkflowConfig.js, which deliberately stays V1-shaped for "Well Life".
export const WLC_ADULT_ADHD_V2_WORKFLOW_CONFIG = Object.freeze({
  ...DEFAULT_WORKFLOW_CONFIG,
  ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
  dfModel: DF_MODELS.FLAT,
  dfPreference: DF_PREFERENCES.END_OF_SSI,
  ddStyle: DD_STYLES.MDT_REVIEW,
  assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.WLC_ADHD,
  replaceCARwithReportBuilder: true,
  ssiFlow: Object.freeze({
    endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
    emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
  }),
  // Explicit: hasMultiStepDD only infers multi-step for tocAdhd, so a wlcAdhd
  // stamp must carry the structure itself.
  // mdtInfoEmailGate: one WLC clinician does CI+SSI in one sitting, so the
  // MDT email waits for both reports to be approved.
  ddFlow: Object.freeze({
    structure: DD_STRUCTURES.MULTI_STEP,
    mdtInfoEmailGate: MDT_INFO_EMAIL_GATES.BOTH_REPORTS_APPROVED,
  }),
  padFlow: Object.freeze({
    structure: PAD_STRUCTURES.FULL,
    // Agreed Medication Plan stage turned off: WLC records medication planning
    // off-platform, so the PAD ends at Health Checks (see PAD_MEDICATION_PLAN_STATES).
    agreedMedicationPlan: PAD_MEDICATION_PLAN_STATES.DISABLED,
  }),
  // QbTest stays disabled via DEFAULT_SUPPLEMENTARY_DOCUMENTS. Historically
  // QbTest was enabled org-wide; legacy patients retain access via the
  // workflowIncludesQbTest patient-level override (admin/backfill_wlc_qbtest.py).
  supplementaryDocuments: {
    ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
    sembleMdtReport: Object.freeze({ enabled: true }),
  },
  appointmentDates: Object.freeze({ enabled: true }),
  // CYP ADHD pathway keys (assessmentModule 'cypAdhd'). Always-explicit
  // stamps: CYP patients share this org assessmentWorkflow object with the
  // adult pathway, so every CYP key is written out in full here and CYP
  // pages read only the wf.cyp* helpers — never the adult keys above.
  cypAdhdFlow: Object.freeze({
    ssiEndPage: CYP_SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
    emergingDifferentials: CYP_SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
    ddStructure: CYP_DD_STRUCTURES.FINAL_FORMULATION,
    differentialsDecisions: CYP_DD_DIFFERENTIALS_DECISIONS_STATES.ENABLED,
    // Both WLC and TOC (Oct 2026): the CYP Decision for ADHD is Fully met or
    // Not met only. New patients only — the stamp is immutable.
    hidePartiallyMet: true,
    schoolRepNomination: CYP_SCHOOL_REP_NOMINATION_STATES.OPTIONAL,
    riskAssessment: CYP_RISK_ASSESSMENT_STRUCTURES.STRUCTURED_V1,
    boaCapture: CYP_BOA_CAPTURE_STATES.DISABLED,
    mdtSupportingClinician: CYP_MDT_SUPPORTING_CLINICIAN_STATES.ENABLED,
    symptomOnsetCapture: CYP_SYMPTOM_ONSET_CAPTURE_STATES.THIRTEEN_PLUS_BEFORE_ONLY,
    // noObservationInSsi: the CI keeps the MSE. observationInSsi is the
    // other variant (the SSI opens with the observation; the CI has no MSE).
    mse: CYP_MSE_STRUCTURES.NO_OBSERVATION_IN_SSI,
    // WLC (Oct 2026): NICE NG87 cardiac/medical contraindication screening
    // on the CI Medical and Family History steps, read back on the MDT
    // Review and PAD Health Checks. New patients only (the stamp is
    // immutable); run backfill_org_documents.py --cyp-workflow after deploy.
    medicalScreening: CYP_MEDICAL_SCREENING_STATES.CONTRAINDICATIONS_V1,
  }),
});

// ── ADHD Clinical UK (adult ADHD only) ──────────────────────────────────────
// Private provider that runs the same adult assessment as Well Life Clinic's
// V2 workflow, with two things deliberately left off:
//
//   1. Semble MDT Plan / Report. WLC records the finished assessment into
//      the Semble EHR. ADHD Clinical UK does not, so sembleMdtReport stays
//      at the disabled default and the MDT Plan page never appears.
//   2. The CYP ADHD pathway. This clinic assesses adults only for now.
//      There is no cypAdhdFlow block, and availableAssessmentModules stays
//      ["adultAdhd"]. Adding the child pathway later is a separate decision.
//
// What the clinic does run, in one clinician's hands except for the MDT:
//   Lead clinician: Clinical Interview, then Semi-Structured Interview
//     (formulation is deferred to the end of the SSI, same as WLC).
//   Lead + one supporting clinician: Diagnostic Decision / MDT Review.
//   Lead alone: Post-Assessment Discussion, ending at Health Checks.
//     The Agreed Medication Plan stage is off — medication planning is
//     recorded off-platform, same as WLC.
//
// The Clinical Interview keeps both the Mental State Examination and the
// Risk Assessment. MSE is removed only by listing "Mental Status
// Examination" in ciExclusions (Psychological Consultations is the org that
// does that). Adult ADHD Risk Assessment is a normal CI section; the
// ciFlow.riskAssessment switch applies to the Adult Autism History module
// only, so it is left unset here.
//
// No QbTest, QbCheck, Pre-Assessment Report, or Bupa letter. Those stay at
// the all-disabled supplementary-document default.
//
// assessmentWorkflowType is wlcAdhd because that is the report pipeline
// (conditional Google Docs, Report Builder, full PAD), not a WLC brand
// flag. The clinic's own letterhead and support email are separate config.
//
// Python mirror: ADHD_CLINICAL_UK_WORKFLOW_CONFIG in
// python-functions/constants/workflow_config.py. Keep the two in step.
export const ADHD_CLINICAL_UK_WORKFLOW_CONFIG = Object.freeze({
  ...DEFAULT_WORKFLOW_CONFIG,
  ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
  dfModel: DF_MODELS.FLAT,
  dfPreference: DF_PREFERENCES.END_OF_SSI,
  ddStyle: DD_STYLES.MDT_REVIEW,
  assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.WLC_ADHD,
  replaceCARwithReportBuilder: true,
  // Empty on purpose: nothing is stripped from the Clinical Interview, so
  // the Mental State Examination stays in the module.
  ciExclusions: [],
  ssiFlow: Object.freeze({
    endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
    emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
  }),
  // One clinician does CI and SSI in the same sitting, so the email that
  // says the MDT information is ready waits until both reports are approved.
  ddFlow: Object.freeze({
    structure: DD_STRUCTURES.MULTI_STEP,
    mdtInfoEmailGate: MDT_INFO_EMAIL_GATES.BOTH_REPORTS_APPROVED,
  }),
  padFlow: Object.freeze({
    structure: PAD_STRUCTURES.FULL,
    agreedMedicationPlan: PAD_MEDICATION_PLAN_STATES.DISABLED,
  }),
  // Explicit copy of the all-disabled default so a reader can see that
  // Semble, QbTest, QbCheck, the Pre-Assessment Report and the Bupa letter
  // are all off, rather than wondering whether a key was forgotten.
  supplementaryDocuments: {
    ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
  },
  appointmentDates: Object.freeze({ enabled: true }),
});

// ── SLaM / 3 Borough one-clinician body ─────────────────────────────────────
// The "one clinician, one appointment" service model adopted by SLaM
// (3 Borough Adult ADHD and Autism Service) in Oct 2026. A single Lead
// clinician runs the Clinical Interview and then the Semi-Structured Interview
// in the same appointment:
//
//   CI (sections 1-11, Diagnostic Formulation deferred)
//     -> end-of-CI decision (ciFlow.endAssessmentDecision):
//          proceed to the SSI, or end the assessment with a written reason
//   SSI -> Review & Synthesis (emerging differentials + emerging formulation
//          recorded ONCE, here)
//     -> appointment finish path (ssiFlow.appointmentFinishPath):
//          share the outcome now and start the PAD, or end the appointment,
//          generate the report and do DD then PAD later
//   DD (multi-step, Final Formulation) -> PAD (full, treatment-first)
//
// What this deliberately turns OFF compared with the two-clinician preset the
// same orgs ran before (SSI-first, a separate SSI clinician, CI Review &
// Synthesis, Lead override of a mismatching SSI):
//   - moduleOrder is CI-first and dfPreference endOfSSI, so the CI ends at
//     Risk Assessment like WLC / TOC and there is no CI Review & Synthesis
//     (no ciFlow.endPage).
//   - ssiFlow.leadOverride DISABLED: the Lead wrote the SSI, so there is no
//     second clinician's decision to reconcile and the DD mismatch / takeover
//     flow never applies.
//   - ssiFlow.emergingDifferentials OPTIONAL: the old preset bypassed the
//     differentials gate through hasCIReview; without an explicit stamp the
//     slamAdhd default would newly block Complete on the SSI Review page.
//   - ddFlow.mdtInfoEmailGate BOTH_REPORTS_APPROVED: one clinician signs off
//     both reports, so the MDT-information email waits for both (as WLC/TOC).
//
// assessmentWorkflowType stays SLAM_ADHD: the report pipeline, {{IF:SLAM}}
// markers, full treatment-first PAD, Report Builder and PAD staleness cues are
// unchanged. Urban ADHD keeps the two-clinician SSI-first preset below.
//
// Existing SLaM / 3 Borough patients are NOT affected: their stamped
// workflowConfig is immutable and deriveLegacyConfig() stays SSI-first for
// unstamped legacy patients. Defined once and shared by both org entries;
// a parity test pins the relationship.
//
// After updating this body, run in each environment:
//   backfill_org_documents.py --workflow --org-name "SLaM"
//   backfill_org_documents.py --workflow --org-name "3 Borough Adult ADHD and Autism Service"
export const SLAM_ONE_CLINICIAN_WORKFLOW_CONFIG = Object.freeze({
  ...DEFAULT_WORKFLOW_CONFIG,
  moduleOrder: MODULE_ORDERS.CI_FIRST,
  dfPreference: DF_PREFERENCES.END_OF_SSI,
  ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
  dfModel: DF_MODELS.FLAT,
  assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.SLAM_ADHD,
  replaceCARwithReportBuilder: true,
  ssiFlow: Object.freeze({
    endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
    leadOverride: SSI_LEAD_OVERRIDE_STATES.DISABLED,
    emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
    appointmentFinishPath: SSI_APPOINTMENT_FINISH_PATH_STATES.ENABLED,
  }),
  ciFlow: Object.freeze({
    endAssessmentDecision: CI_END_ASSESSMENT_DECISION_STATES.ENABLED,
  }),
  ddFlow: Object.freeze({
    structure: DD_STRUCTURES.MULTI_STEP,
    mdtInfoEmailGate: MDT_INFO_EMAIL_GATES.BOTH_REPORTS_APPROVED,
  }),
  // AMP off (medication planning off-platform) + Treatment Options before
  // Strategies in the clinician PAD workflow. Unchanged from the previous
  // SLaM preset.
  padFlow: Object.freeze({
    agreedMedicationPlan: PAD_MEDICATION_PLAN_STATES.DISABLED,
    stageOrder: PAD_STAGE_ORDERS.TREATMENT_FIRST,
  }),
  // Per-module appointment dates on Patient Overview — preferred over
  // inferred firstCompletedAt when rendering report placeholders.
  appointmentDates: Object.freeze({ enabled: true }),
});

export const ORG_WORKFLOW_CONFIGS = Object.freeze({
  // ── Default / testing / demos ──────────────────────────────────────────
  default: { ...DEFAULT_WORKFLOW_CONFIG },
  'ThinkDivergent': { ...DEFAULT_WORKFLOW_CONFIG },
  'ThinkDivergent Admin': { ...DEFAULT_WORKFLOW_CONFIG },

  // ── Phoenix ────────────────────────────────────────────────────────────
  // Standard workflow: CI(DF) → SSI → DD → PAD.
  // SSI gated on ADHD PPD.  Hierarchical DF.
  // QbCheck (AI-reviewed) + BUPA Letter enabled.
  //
  // DEPLOY PREREQUISITE: padFlow.adhdControlledDrugPrescribing requires the
  // Phoenix org document's assessmentWorkflow to be updated BEFORE go-live so
  // setupNewPatient stamps it onto newly created patients. Run:
  //   python org_bootstrap.py --env <env> --org "Phoenix Mental Health Services" --update-workflow
  // on each environment after deploy. No patient-level backfill is needed
  // (the feature is intentionally absent from existing patients per FR9).
  'Phoenix Mental Health Services': {
    ...DEFAULT_WORKFLOW_CONFIG,
    padFlow: Object.freeze({ adhdControlledDrugPrescribing: 'enabled' }),
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      qbCheck: Object.freeze({ enabled: true, mode: QB_CHECK_MODES.AI_REVIEW }),
      bupaLetter: Object.freeze({ enabled: true }),
    },
  },

  // ── SLaM / 3 Borough ──────────────────────────────────────────────────
  // One-clinician, one-appointment workflow (Oct 2026): see the shared
  // SLAM_ONE_CLINICIAN_WORKFLOW_CONFIG body above for the full rationale.
  // "SLaM" is the staging / pilot org; "3 Borough Adult ADHD and Autism
  // Service" is the official production name. Both are identical.
  'SLaM': { ...SLAM_ONE_CLINICIAN_WORKFLOW_CONFIG },
  '3 Borough Adult ADHD and Autism Service': { ...SLAM_ONE_CLINICIAN_WORKFLOW_CONFIG },

  // ── Urban ADHD ────────────────────────────────────────────────────────
  // Private provider running the ORIGINAL SLaM two-clinician, SSI-first
  // clinical workflow: clinician A completes the SSI (Review & Synthesis),
  // then the Lead clinician B completes CI (Review & Synthesis), multi-step DD
  // and the full PAD. Reuses assessmentWorkflowType SLAM_ADHD so the
  // conditional report pipeline, full-PAD placeholders and {{IF:SLAM}} marker
  // all apply without a duplicated rendering stack — SLAM_ADHD identifies the
  // clinical workflow shape, not NHS branding.
  //
  // Urban ADHD did NOT move with SLaM / 3 Borough to the one-clinician model
  // (Oct 2026); this entry is now the reference two-clinician preset and the
  // only org still stamping ciFlow.endPage + ssiFlow.leadOverride.
  //
  // Deliberate differences from SLaM / 3 Borough (all private-org concerns):
  //   - No supplementary documents (inherits all-disabled defaults).
  //   - Not an NHS org: no SSO, no SLaM crisis copy, no granular ethnicity
  //     override. Those are gated on exact org-name sets this org is
  //     intentionally NOT added to (see isSlamNhsOrg in patientSetupConfig).
  //   - Pre-appointment questionnaires mirror Phoenix, not SLaM (configured
  //     on the org's instruments doc via provider_config.py).
  //
  // After updating this config, run in each environment:
  //   backfill_org_documents.py --workflow --org-name "Urban ADHD"
  'Urban ADHD': {
    ...DEFAULT_WORKFLOW_CONFIG,
    moduleOrder: MODULE_ORDERS.SSI_FIRST,
    ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
    dfModel: DF_MODELS.FLAT,
    assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.SLAM_ADHD,
    // Report Builder from day one (no standalone Cover Page).
    replaceCARwithReportBuilder: true,
    // leadOverride: ENABLED — see SLaM preset comment above.
    ssiFlow: Object.freeze({
      endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
      leadOverride: SSI_LEAD_OVERRIDE_STATES.ENABLED,
    }),
    ciFlow: Object.freeze({ endPage: CI_END_PAGES.REVIEW_AND_SYNTHESIS }),
    ddFlow: Object.freeze({ structure: DD_STRUCTURES.MULTI_STEP }),
    // AMP off (medication planning off-platform). Stage order is the default
    // (Strategies first), matching WLC / Phoenix — unlike SLaM which runs
    // Treatment Options first.
    padFlow: Object.freeze({
      agreedMedicationPlan: PAD_MEDICATION_PLAN_STATES.DISABLED,
    }),
    // Per-module appointment dates on Patient Overview.
    appointmentDates: Object.freeze({ enabled: true }),
  },

  // ── Well Life Clinic (production) ──────────────────────────────────────
  // The shared WLC V2 body: adult ADHD + CYP ADHD.
  //
  // Report templates for this org keep BOTH the V1 Google Docs (base keys,
  // for patients mid-assessment or completed on the standard workflow) and
  // the V2 conditional Docs (" - Conditional" keys) — see
  // python-functions/constants/report_templates.py and get_template_id().
  'Well Life Clinic': { ...WLC_ADULT_ADHD_V2_WORKFLOW_CONFIG },

  // ── ADHD Clinical UK ───────────────────────────────────────────────────
  // Adult-only private clinic on the WLC V2 clinical workflow, without
  // Semble and without CYP ADHD. See ADHD_CLINICAL_UK_WORKFLOW_CONFIG.
  'ADHD Clinical UK': { ...ADHD_CLINICAL_UK_WORKFLOW_CONFIG },

  // ── The Owl Centre ─────────────────────────────────────────────────────
  // MDT Review org with flat differentials, deferred DF (end of SSI),
  // and SSI always accessible.  QbCheck in passthrough mode — scores
  // entered manually, auto-approved.
  // Single clinician does CI+SSI in one appointment; DF deferred to SSI
  // Review & Synthesis.  Multi-step DD (4-page wizard with sidebar).
  //
  // After updating this config, run in each environment:
  //   backfill_org_documents.py --workflow --org-name "The Owl Centre"
  'The Owl Centre': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
    dfModel: DF_MODELS.FLAT,
    dfPreference: DF_PREFERENCES.END_OF_SSI,
    ddStyle: DD_STYLES.MDT_REVIEW,
    assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD,
    ssiFlow: Object.freeze({
      endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
      domainsImpacted: SSI_DOMAINS_IMPACTED_STATES.DISABLED,
      emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
    }),
    // Risk Assessment turned off in the Adult Autism History (Clinical Interview)
    // module: TOC clinicians record risk on their own patient-management platform
    // (NEST). Only the autism History paths read this flag, so TOC's ADHD CI is
    // unaffected. See CI_RISK_ASSESSMENT_STATES.
    ciFlow: Object.freeze({ riskAssessment: CI_RISK_ASSESSMENT_STATES.DISABLED }),
    // Behavioural Observation redesign (v2): tagged notes + criteria-level
    // evidence + criteria notes + conditional Other Behaviours paragraph. Only
    // new TOC patients stamped after the org backfill pick this up; existing
    // patients keep v1. See OBS_VERSIONS.
    obsFlow: Object.freeze({ version: OBS_VERSIONS.V2 }),
    // Multi-step DD with differentials decisions disabled: clinicians skip
    // the "Decision on Differentials" page and instead see a read-only list
    // of emerging differentials on the Final Formulation page. The new PAD
    // Next Steps toggle (includeAutismFollowUp) replaces the old per-condition
    // autism referral logic that depended on DD section 2 decisions.
    // mdtInfoEmailGate: the same TOC clinician does CI+SSI in one sitting, so
    // the MDT email waits for both reports to be approved.
    ddFlow: Object.freeze({
      structure: DD_STRUCTURES.MULTI_STEP,
      differentialsDecisions: DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED,
      mdtInfoEmailGate: MDT_INFO_EMAIL_GATES.BOTH_REPORTS_APPROVED,
    }),
    // Adult Autism DD: differentials decisions disabled, mirroring the ADHD
    // pathway above. Kept as a separate namespace so the two pathways can be
    // independently toggled per org in the future (follows the cypAdhdFlow
    // precedent for pathway-specific config).
    autismDdFlow: Object.freeze({
      differentialsDecisions: DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED,
    }),
    // CYP ADHD pathway keys (assessmentModule 'cypAdhd'). Starts as a mirror
    // of Well Life Clinic's CYP flow so requirements conversations with TOC
    // happen against a live system; every TOC-specific CYP behaviour agreed in
    // requirements gathering becomes a key here (never in the adult namespaces
    // above — CYP pages read only the wf.cyp* helpers). Note ciFlow's
    // riskAssessment: DISABLED above does NOT reach CYP: the CYP Clinical
    // Interview always includes the Risk Assessment.
    cypAdhdFlow: Object.freeze({
      ssiEndPage: CYP_SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
      emergingDifferentials: CYP_SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
      ddStructure: CYP_DD_STRUCTURES.FINAL_FORMULATION,
      // TOC (Oct 2026): no Decision on Differentials step, matching TOC's
      // adult ADHD and adult Autism DDs (ddFlow / autismDdFlow above). The
      // emerging differentials are listed read-only on Final Formulation.
      // Deliberate divergence from WLC V2's ENABLED.
      differentialsDecisions: CYP_DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED,
      // Both WLC and TOC (Oct 2026): no "Partially met" on the CYP Decision
      // for ADHD. New patients only.
      hidePartiallyMet: true,
      // TOC (Sept 2026): a child in school MUST have a school representative
      // nominated before the parent's questionnaires unlock. Deliberate
      // divergence from WLC V2's OPTIONAL.
      schoolRepNomination: CYP_SCHOOL_REP_NOMINATION_STATES.REQUIRED_WHEN_IN_SCHOOL,
      // TOC (Sept 2026): NEST-parity structured risk assessment in the CI.
      riskAssessment: CYP_RISK_ASSESSMENT_STRUCTURES.STRUCTURED_V1,
      // TOC (23 Sept standup): BOA summary captured at the start of the MDT
      // Review — capture only, report wiring deferred.
      boaCapture: CYP_BOA_CAPTURE_STATES.MDT_REVIEW_TAB,
      // Both WLC and TOC (TDI-208): the lead clinician brings a supporting
      // clinician into the MDT meeting and the diagnostic decision is made
      // jointly. WLC aligned Oct 2026.
      mdtSupportingClinician: CYP_MDT_SUPPORTING_CLINICIAN_STATES.ENABLED,
      // Both WLC and TOC: "present before age 12" asked only when the child
      // is 13 or older — a younger child is inside the age-12 window, so
      // onset is implied by persistence. TOC was originally stamped with the
      // onset pair (TDI-205) but the divergence had no clinical basis for
      // children under 12; aligned Oct 2026.
      symptomOnsetCapture:
        CYP_SYMPTOM_ONSET_CAPTURE_STATES.THIRTEEN_PLUS_BEFORE_ONLY,
      // noObservationInSsi, the pair-mate of observationInSsi. The CI keeps
      // the Mental State Examination (child-specific Clinical Observation
      // questions) and the SSI does not open with an observation phase.
      // WLC stamps the same value. Set this to OBSERVATION_IN_SSI to take
      // the other variant. No CYP ADHD patients exist yet.
      mse: CYP_MSE_STRUCTURES.NO_OBSERVATION_IN_SSI,
      // Diverges from WLC: the contraindication screening panel was a WLC
      // request (Oct 2026); TOC has not asked for it, so it stays off until
      // requirements gathering says otherwise.
      medicalScreening: CYP_MEDICAL_SCREENING_STATES.DISABLED,
    }),
    // TOC Autism form trim: removes clinically unnecessary questions from
    // the Autism pre-appointment forms and History proforma (e.g. Women's
    // Health, Forensic History, Driving, parental ages). The profile is
    // stamped onto new patients via workflowConfig; existing unstamped
    // patients are unaffected unless explicitly backfilled.
    assessmentFormProfile: 'owlCentreAutismV1',
    // Per-module appointment dates on Patient Overview — preferred over
    // inferred firstCompletedAt when rendering report placeholders.
    appointmentDates: Object.freeze({ enabled: true }),
    // Appointment Checklist: start/end-of-appointment prompt shown on the
    // Adult Autism proforma pages (History, SSI, OBS). Frontend-only UI.
    appointmentChecklist: Object.freeze({ enabled: true }),
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      qbCheck: Object.freeze({ enabled: true, mode: QB_CHECK_MODES.PASSTHROUGH }),
    },
  },

  // ── Demo Org ───────────────────────────────────────────────────────────
  // Mirrors The Owl Centre workflow for demonstration/testing purposes.
  // Full TOC ADHD workflow: MDT Review, flat DF, deferred DF (end of SSI),
  // SSI always accessible, QbCheck passthrough, multi-step DD.
  //
  // After updating this config, run in each environment:
  //   backfill_org_documents.py --workflow --org-name "Demo Org"
  'Demo Org': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
    dfModel: DF_MODELS.FLAT,
    dfPreference: DF_PREFERENCES.END_OF_SSI,
    ddStyle: DD_STYLES.MDT_REVIEW,
    assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD,
    ssiFlow: Object.freeze({
      endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
      domainsImpacted: SSI_DOMAINS_IMPACTED_STATES.DISABLED,
      emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.OPTIONAL,
    }),
    // Mirror TOC: Risk Assessment off in the Adult Autism History module so the
    // demo faithfully reflects the live TOC autism flow.
    ciFlow: Object.freeze({ riskAssessment: CI_RISK_ASSESSMENT_STATES.DISABLED }),
    // Mirror TOC: Behavioural Observation redesign (v2) so demo patients exercise
    // the real tagged-notes / criteria-evidence pipeline end to end.
    obsFlow: Object.freeze({ version: OBS_VERSIONS.V2 }),
    // Mirror TOC: MDT email waits for both CI and SSI reports.
    ddFlow: Object.freeze({
      structure: DD_STRUCTURES.MULTI_STEP,
      mdtInfoEmailGate: MDT_INFO_EMAIL_GATES.BOTH_REPORTS_APPROVED,
    }),
    // Mirror TOC: Adult Autism DD differentials decisions disabled so demo
    // patients exercise the same read-only emerging differentials panel and
    // PAD autism follow-up toggle as live TOC patients.
    autismDdFlow: Object.freeze({
      differentialsDecisions: DD_DIFFERENTIALS_DECISIONS_STATES.DISABLED,
    }),
    // Mirror TOC: Per-module appointment dates on Patient Overview.
    appointmentDates: Object.freeze({ enabled: true }),
    // Mirror TOC: Appointment Checklist enabled for demo fidelity.
    appointmentChecklist: Object.freeze({ enabled: true }),
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      qbCheck: Object.freeze({ enabled: true, mode: QB_CHECK_MODES.PASSTHROUGH }),
    },
  },

  // ── ADHD and Autism Associates ─────────────────────────────────────────
  // MDT Review org, standard module order.
  // Pre-Assessment Report enabled (only org using this feature).
  'ADHD and Autism Associates': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ddStyle: DD_STYLES.MDT_REVIEW,
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      preAssessmentReport: Object.freeze({ enabled: true }),
    },
  },

  // ── Lancaster Medical Practice ─────────────────────────────────────────
  // TOC-like MDT dual-pathway org (staging first; not yet live in prod).
  // Shares TOC's core ADHD workflow — MDT Review, flat DF, deferred DF (end of
  // SSI), SSI always accessible, multi-step DD — with three deliberate
  // differences from The Owl Centre:
  //   1. No QbCheck (qbCheck left at the disabled default — no override here).
  //   2. No Behavioural Observation on Adult Autism (obsFlow.observation
  //      DISABLED). The lead clinician who ran History/SSI keeps DD write
  //      access; a supporting clinician still joins the DD (MDT review).
  //   3. Patient rating scales on both pathways (configured on the org's
  //      instruments doc, not here).
  // Both Adult ADHD and Adult Autism pathways are enabled via
  // availableAssessmentModules.
  //
  // After updating this config, run in each environment:
  //   backfill_org_documents.py --workflow --org-name "Lancaster Medical Practice"
  //   backfill_org_documents.py --modules --org-name "Lancaster Medical Practice"
  //   backfill_org_documents.py --llm-prompt-version 2 --org-name "Lancaster Medical Practice"
  // (Instruments are set via Provider Settings / Firestore, not --instruments.)
  'Lancaster Medical Practice': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ssiAccess: SSI_ACCESS_POLICIES.ALWAYS,
    dfModel: DF_MODELS.FLAT,
    dfPreference: DF_PREFERENCES.END_OF_SSI,
    ddStyle: DD_STYLES.MDT_REVIEW,
    assessmentWorkflowType: ASSESSMENT_WORKFLOW_TYPES.TOC_ADHD,
    // Explicit 'required' for emergingDifferentials: LMP shares
    // assessmentWorkflowType 'tocAdhd' with TOC, so without an explicit stamp
    // the runtime fallback in useWorkflowConfig would incorrectly make
    // differentials optional for LMP patients (matching TOC). LMP clinicians
    // make per-condition decisions in DD and need mandatory differentials.
    ssiFlow: Object.freeze({
      endPage: SSI_END_PAGES.REVIEW_AND_SYNTHESIS,
      emergingDifferentials: SSI_EMERGING_DIFFERENTIALS_STATES.REQUIRED,
    }),
    // Mirror TOC: Risk Assessment off in the Adult Autism History module.
    // LMP can re-enable later if they record risk in-platform.
    ciFlow: Object.freeze({ riskAssessment: CI_RISK_ASSESSMENT_STATES.DISABLED }),
    // Behavioural Observation turned off for Adult Autism (see comment above).
    obsFlow: Object.freeze({ observation: OBS_OBSERVATION_STATES.DISABLED }),
    // Explicit 'enabled' on both ddFlow and autismDdFlow: LMP shares
    // assessmentWorkflowType 'tocAdhd' with TOC, so without an explicit stamp
    // the runtime fallback in useWorkflowConfig would incorrectly disable
    // differentials decisions for LMP patients. These explicit 'enabled' stamps
    // override the fallback for both new patients (via stamp) and ensure the
    // validator documents the intended state.
    ddFlow: Object.freeze({
      structure: DD_STRUCTURES.MULTI_STEP,
      differentialsDecisions: DD_DIFFERENTIALS_DECISIONS_STATES.ENABLED,
    }),
    autismDdFlow: Object.freeze({
      differentialsDecisions: DD_DIFFERENTIALS_DECISIONS_STATES.ENABLED,
    }),
    // Full PAD (DIS/TRT/HLT) instead of TOC's Next Steps page. LMP clinicians
    // run strategies, treatment preferences, and health checks — matching SLaM's
    // PAD content without the SSI-first workflow or lead override. AMP disabled
    // (medication planning happens off-platform).
    padFlow: Object.freeze({
      structure: PAD_STRUCTURES.FULL,
      agreedMedicationPlan: PAD_MEDICATION_PLAN_STATES.DISABLED,
    }),
  },

  // ── MDT Org (internal testing) ─────────────────────────────────────────
  // QbTest enabled for internal testing.
  'MDT Org': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ddStyle: DD_STYLES.MDT_REVIEW,
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      qbTest: Object.freeze({ enabled: true }),
    },
  },

  // ── The Spectrum Mind Clinic ───────────────────────────────────────────
  // QbCheck (AI-reviewed) enabled.
  'The Spectrum Mind Clinic': {
    ...DEFAULT_WORKFLOW_CONFIG,
    supplementaryDocuments: {
      ...DEFAULT_SUPPLEMENTARY_DOCUMENTS,
      qbCheck: Object.freeze({ enabled: true, mode: QB_CHECK_MODES.AI_REVIEW }),
    },
  },

  // ── Psychological Consultations ────────────────────────────────────────
  // Standard workflow but Mental Status Examination is excluded from CI.
  'Psychological Consultations': {
    ...DEFAULT_WORKFLOW_CONFIG,
    ciExclusions: ['Mental Status Examination'],
  },
});

// ---------------------------------------------------------------------------
// Workflow Decisions — mutable per-patient runtime state
// ---------------------------------------------------------------------------

/**
 * Default values for `workflowDecisions`, the mutable state that tracks
 * clinician choices during an assessment.  Stored on the patient document
 * alongside the immutable `workflowConfig`.
 *
 * Intentionally empty.  Decision keys are added only when the clinician
 * explicitly makes a choice at runtime.  An absent key means "no decision
 * yet", which allows the resolution logic in resolveWorkflowConfig to fall
 * back to the organisation's preference (e.g. dfPreference → isDFDeferred
 * default).  Stamping a false here would prevent that fallback from ever
 * triggering.
 *
 * Keys in use:
 *   dfDeferred     boolean — overrides dfPreference (no production writer yet)
 *   ciFinishPath   CI_FINISH_PATHS — appointment finish path (PAD now / defer)
 *   ciEndDecision  CI_END_DECISIONS — end-of-CI proceed / end assessment
 */
export const DEFAULT_WORKFLOW_DECISIONS = Object.freeze({});

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const VALID_DF_PREFERENCES = Object.values(DF_PREFERENCES);
const VALID_SSI_ACCESS_POLICIES = Object.values(SSI_ACCESS_POLICIES);
const VALID_DF_MODELS = Object.values(DF_MODELS);
const VALID_DD_STYLES = Object.values(DD_STYLES);
const VALID_QB_CHECK_MODES = Object.values(QB_CHECK_MODES);
const VALID_PAD_MEDICATION_PLAN_STATES = Object.values(PAD_MEDICATION_PLAN_STATES);
const VALID_PAD_STAGE_ORDERS = Object.values(PAD_STAGE_ORDERS);
const VALID_PAD_STRUCTURES = Object.values(PAD_STRUCTURES);
const VALID_SUPPLEMENTARY_DOC_KEYS = Object.keys(DEFAULT_SUPPLEMENTARY_DOCUMENTS);

/**
 * Validates an assessmentWorkflow config object.  Returns an array of error
 * strings (empty if valid).  Designed for use in admin scripts and dev-mode
 * guards, NOT for runtime gating — invalid configs should fall back to
 * DEFAULT_WORKFLOW_CONFIG rather than crashing.
 *
 * @param {Object} config - The assessmentWorkflow object to validate.
 * @param {string} [label] - Optional label for error messages (e.g. org name).
 * @returns {string[]} Array of validation error messages (empty = valid).
 */
export const validateWorkflowConfig = (config, label = 'config') => {
  const errors = [];

  if (!config || typeof config !== 'object') {
    errors.push(`${label}: config must be a non-null object`);
    return errors;
  }

  // moduleOrder
  if (!Array.isArray(config.moduleOrder)) {
    errors.push(`${label}: moduleOrder must be an array`);
  } else {
    const required = ['ci', 'ssi', 'dd', 'pad'];
    const missing = required.filter((m) => !config.moduleOrder.includes(m));
    if (missing.length > 0) {
      errors.push(`${label}: moduleOrder is missing modules: ${missing.join(', ')}`);
    }
  }

  // dfPreference
  if (!VALID_DF_PREFERENCES.includes(config.dfPreference)) {
    errors.push(
      `${label}: dfPreference must be one of ${VALID_DF_PREFERENCES.join(', ')}, got "${config.dfPreference}"`
    );
  }

  // ssiAccess
  if (!VALID_SSI_ACCESS_POLICIES.includes(config.ssiAccess)) {
    errors.push(
      `${label}: ssiAccess must be one of ${VALID_SSI_ACCESS_POLICIES.join(', ')}, got "${config.ssiAccess}"`
    );
  }

  // dfModel
  if (!VALID_DF_MODELS.includes(config.dfModel)) {
    errors.push(
      `${label}: dfModel must be one of ${VALID_DF_MODELS.join(', ')}, got "${config.dfModel}"`
    );
  }

  // ddStyle
  if (!VALID_DD_STYLES.includes(config.ddStyle)) {
    errors.push(
      `${label}: ddStyle must be one of ${VALID_DD_STYLES.join(', ')}, got "${config.ddStyle}"`
    );
  }

  // ciExclusions
  if (config.ciExclusions !== undefined && !Array.isArray(config.ciExclusions)) {
    errors.push(`${label}: ciExclusions must be an array if provided`);
  }

  // ssiFlow (optional nested object)
  const ssiFlow = config.ssiFlow;
  if (ssiFlow !== undefined) {
    if (typeof ssiFlow !== 'object' || ssiFlow === null) {
      errors.push(`${label}: ssiFlow must be an object if provided`);
    } else {
      if (ssiFlow.endPage !== undefined && !Object.values(SSI_END_PAGES).includes(ssiFlow.endPage)) {
        errors.push(
          `${label}: ssiFlow.endPage must be one of ${Object.values(SSI_END_PAGES).join(', ')}, got "${ssiFlow.endPage}"`
        );
      }
      if (
        ssiFlow.leadOverride !== undefined &&
        !Object.values(SSI_LEAD_OVERRIDE_STATES).includes(ssiFlow.leadOverride)
      ) {
        errors.push(
          `${label}: ssiFlow.leadOverride must be one of ${Object.values(SSI_LEAD_OVERRIDE_STATES).join(', ')}, got "${ssiFlow.leadOverride}"`
        );
      }
      if (
        ssiFlow.domainsImpacted !== undefined &&
        !Object.values(SSI_DOMAINS_IMPACTED_STATES).includes(ssiFlow.domainsImpacted)
      ) {
        errors.push(
          `${label}: ssiFlow.domainsImpacted must be one of ${Object.values(SSI_DOMAINS_IMPACTED_STATES).join(', ')}, got "${ssiFlow.domainsImpacted}"`
        );
      }
      if (
        ssiFlow.emergingDifferentials !== undefined &&
        !Object.values(SSI_EMERGING_DIFFERENTIALS_STATES).includes(ssiFlow.emergingDifferentials)
      ) {
        errors.push(
          `${label}: ssiFlow.emergingDifferentials must be one of ${Object.values(SSI_EMERGING_DIFFERENTIALS_STATES).join(', ')}, got "${ssiFlow.emergingDifferentials}"`
        );
      }
      if (
        ssiFlow.appointmentFinishPath !== undefined &&
        !Object.values(SSI_APPOINTMENT_FINISH_PATH_STATES).includes(ssiFlow.appointmentFinishPath)
      ) {
        errors.push(
          `${label}: ssiFlow.appointmentFinishPath must be one of ${Object.values(SSI_APPOINTMENT_FINISH_PATH_STATES).join(', ')}, got "${ssiFlow.appointmentFinishPath}"`
        );
      }
    }
  }

  // ciFlow (optional nested object)
  const ciFlow = config.ciFlow;
  if (ciFlow !== undefined) {
    if (typeof ciFlow !== 'object' || ciFlow === null) {
      errors.push(`${label}: ciFlow must be an object if provided`);
    } else {
      if (ciFlow.endPage !== undefined && !Object.values(CI_END_PAGES).includes(ciFlow.endPage)) {
        errors.push(
          `${label}: ciFlow.endPage must be one of ${Object.values(CI_END_PAGES).join(', ')}, got "${ciFlow.endPage}"`
        );
      }
      if (
        ciFlow.riskAssessment !== undefined &&
        !Object.values(CI_RISK_ASSESSMENT_STATES).includes(ciFlow.riskAssessment)
      ) {
        errors.push(
          `${label}: ciFlow.riskAssessment must be one of ${Object.values(CI_RISK_ASSESSMENT_STATES).join(', ')}, got "${ciFlow.riskAssessment}"`
        );
      }
      if (
        ciFlow.endAssessmentDecision !== undefined &&
        !Object.values(CI_END_ASSESSMENT_DECISION_STATES).includes(ciFlow.endAssessmentDecision)
      ) {
        errors.push(
          `${label}: ciFlow.endAssessmentDecision must be one of ${Object.values(CI_END_ASSESSMENT_DECISION_STATES).join(', ')}, got "${ciFlow.endAssessmentDecision}"`
        );
      }
    }
  }

  // obsFlow (optional nested object — Adult Autism Behavioural Observation
  // on/off + module version)
  const obsFlow = config.obsFlow;
  if (obsFlow !== undefined) {
    if (typeof obsFlow !== 'object' || obsFlow === null) {
      errors.push(`${label}: obsFlow must be an object if provided`);
    } else {
      if (
        obsFlow.observation !== undefined &&
        !Object.values(OBS_OBSERVATION_STATES).includes(obsFlow.observation)
      ) {
        errors.push(
          `${label}: obsFlow.observation must be one of ${Object.values(OBS_OBSERVATION_STATES).join(', ')}, got "${obsFlow.observation}"`
        );
      }
      if (
        obsFlow.version !== undefined &&
        !Object.values(OBS_VERSIONS).includes(obsFlow.version)
      ) {
        errors.push(
          `${label}: obsFlow.version must be one of ${Object.values(OBS_VERSIONS).join(', ')}, got "${obsFlow.version}"`
        );
      }
    }
  }

  // ddFlow (optional nested object)
  const ddFlow = config.ddFlow;
  if (ddFlow !== undefined) {
    if (typeof ddFlow !== 'object' || ddFlow === null) {
      errors.push(`${label}: ddFlow must be an object if provided`);
    } else {
      if (ddFlow.structure !== undefined && !Object.values(DD_STRUCTURES).includes(ddFlow.structure)) {
        errors.push(
          `${label}: ddFlow.structure must be one of ${Object.values(DD_STRUCTURES).join(', ')}, got "${ddFlow.structure}"`
        );
      }
      if (ddFlow.differentialsDecisions !== undefined
          && !Object.values(DD_DIFFERENTIALS_DECISIONS_STATES).includes(ddFlow.differentialsDecisions)) {
        errors.push(
          `${label}: ddFlow.differentialsDecisions must be one of ${Object.values(DD_DIFFERENTIALS_DECISIONS_STATES).join(', ')}, got "${ddFlow.differentialsDecisions}"`
        );
      }
      // A string such as "enabled" would read as "show" at runtime (=== true
      // fails), so anything other than a real boolean is a config error.
      if (ddFlow.hidePartiallyMet !== undefined && typeof ddFlow.hidePartiallyMet !== 'boolean') {
        errors.push(
          `${label}: ddFlow.hidePartiallyMet must be a boolean if provided, got "${ddFlow.hidePartiallyMet}"`
        );
      }
      if (ddFlow.mdtInfoEmailGate !== undefined
          && !Object.values(MDT_INFO_EMAIL_GATES).includes(ddFlow.mdtInfoEmailGate)) {
        errors.push(
          `${label}: ddFlow.mdtInfoEmailGate must be one of ${Object.values(MDT_INFO_EMAIL_GATES).join(', ')}, got "${ddFlow.mdtInfoEmailGate}"`
        );
      }
    }
  }

  // autismDdFlow (optional nested object — Adult Autism DD pathway config,
  // kept separate from ddFlow so the two pathways can be independently tuned)
  const autismDdFlow = config.autismDdFlow;
  if (autismDdFlow !== undefined) {
    if (typeof autismDdFlow !== 'object' || autismDdFlow === null) {
      errors.push(`${label}: autismDdFlow must be an object if provided`);
    } else {
      if (autismDdFlow.differentialsDecisions !== undefined
          && !Object.values(DD_DIFFERENTIALS_DECISIONS_STATES).includes(autismDdFlow.differentialsDecisions)) {
        errors.push(
          `${label}: autismDdFlow.differentialsDecisions must be one of ${Object.values(DD_DIFFERENTIALS_DECISIONS_STATES).join(', ')}, got "${autismDdFlow.differentialsDecisions}"`
        );
      }
    }
  }

  // padFlow (optional nested object)
  const padFlow = config.padFlow;
  if (padFlow !== undefined) {
    if (typeof padFlow !== 'object' || padFlow === null) {
      errors.push(`${label}: padFlow must be an object if provided`);
    } else {
      if (
        padFlow.agreedMedicationPlan !== undefined &&
        !VALID_PAD_MEDICATION_PLAN_STATES.includes(padFlow.agreedMedicationPlan)
      ) {
        errors.push(
          `${label}: padFlow.agreedMedicationPlan must be one of ${VALID_PAD_MEDICATION_PLAN_STATES.join(', ')}, got "${padFlow.agreedMedicationPlan}"`
        );
      }
      if (
        padFlow.stageOrder !== undefined &&
        !VALID_PAD_STAGE_ORDERS.includes(padFlow.stageOrder)
      ) {
        errors.push(
          `${label}: padFlow.stageOrder must be one of ${VALID_PAD_STAGE_ORDERS.join(', ')}, got "${padFlow.stageOrder}"`
        );
      }
      if (
        padFlow.structure !== undefined &&
        !VALID_PAD_STRUCTURES.includes(padFlow.structure)
      ) {
        errors.push(
          `${label}: padFlow.structure must be one of ${VALID_PAD_STRUCTURES.join(', ')}, got "${padFlow.structure}"`
        );
      }
      if (
        padFlow.adhdControlledDrugPrescribing !== undefined &&
        padFlow.adhdControlledDrugPrescribing !== 'enabled'
      ) {
        errors.push(
          `${label}: padFlow.adhdControlledDrugPrescribing must be 'enabled' if provided, got "${padFlow.adhdControlledDrugPrescribing}"`
        );
      }
    }
  }

  // appointmentChecklist (optional nested object — start/end-of-appointment
  // checklist UI for Adult Autism proformas)
  const appointmentChecklist = config.appointmentChecklist;
  if (appointmentChecklist !== undefined) {
    if (typeof appointmentChecklist !== 'object' || appointmentChecklist === null) {
      errors.push(`${label}: appointmentChecklist must be an object if provided`);
    } else if (typeof appointmentChecklist.enabled !== 'boolean') {
      errors.push(`${label}: appointmentChecklist.enabled must be a boolean`);
    }
  }

  // appointmentDates (optional nested object — per-module appointment date UI)
  const appointmentDates = config.appointmentDates;
  if (appointmentDates !== undefined) {
    if (typeof appointmentDates !== 'object' || appointmentDates === null) {
      errors.push(`${label}: appointmentDates must be an object if provided`);
    } else if (typeof appointmentDates.enabled !== 'boolean') {
      errors.push(`${label}: appointmentDates.enabled must be a boolean`);
    }
  }

  // replaceCARwithReportBuilder (optional boolean — Report Builder / no Cover Page)
  if (
    config.replaceCARwithReportBuilder !== undefined &&
    typeof config.replaceCARwithReportBuilder !== 'boolean'
  ) {
    errors.push(`${label}: replaceCARwithReportBuilder must be a boolean if provided`);
  }

  // supplementaryDocuments
  const suppDocs = config.supplementaryDocuments;
  if (suppDocs !== undefined) {
    if (typeof suppDocs !== 'object' || suppDocs === null) {
      errors.push(`${label}: supplementaryDocuments must be an object if provided`);
    } else {
      Object.entries(suppDocs).forEach(([key, value]) => {
        if (!VALID_SUPPLEMENTARY_DOC_KEYS.includes(key)) {
          errors.push(`${label}: supplementaryDocuments contains unknown key "${key}"`);
        }
        if (typeof value !== 'object' || value === null) {
          errors.push(`${label}: supplementaryDocuments.${key} must be an object`);
        } else if (typeof value.enabled !== 'boolean') {
          errors.push(`${label}: supplementaryDocuments.${key}.enabled must be a boolean`);
        }
        if (key === 'qbCheck' && value?.mode !== undefined && !VALID_QB_CHECK_MODES.includes(value.mode)) {
          errors.push(
            `${label}: supplementaryDocuments.qbCheck.mode must be one of ${VALID_QB_CHECK_MODES.join(', ')}, got "${value.mode}"`
          );
        }
      });
    }
  }

  return errors;
};

// ---------------------------------------------------------------------------
// Dev-mode validation of all presets at module load
// ---------------------------------------------------------------------------
if (process.env.NODE_ENV === 'development') {
  Object.entries(ORG_WORKFLOW_CONFIGS).forEach(([orgName, config]) => {
    const errors = validateWorkflowConfig(config, orgName);
    if (errors.length > 0) {
      console.error(`Workflow config validation failed for "${orgName}":`);
      errors.forEach((e) => console.error(`  - ${e}`));
    }
  });
}
